// Small shared primitives — one place, --pm-* vocabulary only (PRODUCT.md).
import { X } from 'lucide-react';

export function Avatar({ name, size = 24 }) {
  const initials = (name || '?')
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span
      title={name}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-pm-raised text-pm-muted font-medium ring-1 ring-inset ring-white/5"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {initials}
    </span>
  );
}

export function LabelChip({ label, small }) {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${
        small ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs'
      }`}
      style={{ background: `${label.color}26`, color: label.color }}
    >
      {label.name}
    </span>
  );
}

export const PRIORITY_META = {
  urgent: { label: 'Urgent', color: 'var(--pm-danger)' },
  high: { label: 'High', color: '#d98a5b' },
  medium: { label: 'Medium', color: '#c8a557' },
  low: { label: 'Low', color: '#5b8dd9' },
  none: { label: 'None', color: 'var(--pm-faint)' },
};

export function PriorityDot({ priority }) {
  if (!priority || priority === 'none') return null;
  const meta = PRIORITY_META[priority] || PRIORITY_META.none;
  return (
    <span title={meta.label} className="inline-flex items-center gap-1 text-xs" style={{ color: meta.color }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.color }} />
      {meta.label}
    </span>
  );
}

export function Modal({ title, onClose, children, wide }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[10vh]"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} rounded-lg border border-pm-border bg-pm-surface pm-modal-in`}
        style={{ boxShadow: 'var(--pm-elev-3)' }}
      >
        <div className="flex items-center justify-between border-b border-pm-border px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-pm-muted hover:bg-pm-raised hover:text-pm-text">
            <X size={16} />
            <span className="sr-only">Close</span>
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

// Form field — a bordered "card" wrapping the label + inset input, matching the
// My Account / ebank field style (raised surface card, darkest input inside).
export function Field({ label, children }) {
  return (
    <label className="block rounded-lg border border-pm-border bg-pm-raised p-3">
      {label ? <span className="mb-1.5 block text-xs font-medium text-pm-muted">{label}</span> : null}
      {children}
    </label>
  );
}

// Focus = ONE calm accent border (the Nifty register). No halo shadow — paired
// with the global input outline suppression in index.css it reads as a single
// subtle line instead of a thick double ring.
export const inputCls =
  'w-full rounded-md border border-pm-border bg-pm-input px-3 py-2 text-sm text-pm-text ' +
  'placeholder:text-pm-faint outline-none transition-colors ' +
  'focus:border-[var(--pm-focus-border)]';

// Native date/time inputs: open the picker when the WHOLE field is clicked, not
// just the calendar icon (showPicker needs a user gesture; guarded for support).
export function openPicker(e) {
  const el = e.currentTarget;
  try {
    if (el && typeof el.showPicker === 'function') el.showPicker();
  } catch {
    /* NotAllowedError / unsupported — native icon still works */
  }
}

export const btnPrimary =
  'rounded-md bg-pm-accent px-3 py-2 text-sm font-semibold text-pm-accent-ink ' +
  'shadow-[var(--pm-shadow-sm)] hover:brightness-[1.06] active:translate-y-px ' +
  'disabled:opacity-60 disabled:hover:brightness-100 disabled:active:translate-y-0';

export const btnGhost =
  'rounded-md border border-pm-border px-3 py-2 text-sm text-pm-muted ' +
  'hover:bg-pm-raised hover:text-pm-text active:translate-y-px';

// Shared card surface — border + tinted elevation + inner top-highlight, with a
// quiet lift on hover. One place so every tile/panel reads as the same material.
export const cardCls =
  'rounded-lg border border-pm-border bg-pm-surface ' +
  'shadow-[var(--pm-elev-1)] transition duration-150 ease-[cubic-bezier(0.16,1,0.3,1)] ' +
  'hover:border-[var(--pm-hover-border)] hover:shadow-[var(--pm-elev-2)]';

// Static elevated surface — same material, no interactive hover. For non-clickable
// containers (lists, columns, stat panels, popovers) that just need depth.
export const panelCls = 'rounded-lg border border-pm-border bg-pm-surface shadow-[var(--pm-elev-1)]';

export function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

export function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
